import React, { useEffect, useRef, useState } from 'react';

import Button from 'components/Buttons/Button';
import ConfirmationModal from 'components/ConfirmationModal';
import CustomSelect, { RenderProps as CustomSelectRenderProps } from 'components/Selects/CustomSelect';
import useStoreTranslation from 'hooks/useStoreTranslation';
import { RepprovalReason } from 'models/repprovalReason';
import OptionsItems from 'screens/components/Dashboard/components/ListAwaiting/OptionsItems';
import { htmlT } from 'utils/generic';

import { Container, AcceptButton, TextModal } from './styles';

interface Props {
  requesterName: string;
  onConfirm?: () => void;
  onDecline?: (reason?: RepprovalReason) => void;
  submitting?: boolean;
  reasons?: RepprovalReason[];
  paymentFailure?: boolean;
}

const Buttons = ({ requesterName, onConfirm, onDecline, submitting, reasons, paymentFailure }: Props) => {
  const { t } = useStoreTranslation();

  const [isShowAcceptBottomSheet, setIsShowAcceptBottomSheet] = useState(false);
  const [isShowDeclineBottomSheet, setIsShowDeclineBottomSheet] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  const openAcceptBottomSheet = () => setIsShowAcceptBottomSheet(true);
  const openDeclineBottomSheet = () => setIsShowDeclineBottomSheet(true);
  const [selectedReason, setSelectedReason] = useState<RepprovalReason>();
  const reasonRef = useRef(null);

  const closeAcceptBottomSheet = () => {
    if (submitting) return;
    setIsShowAcceptBottomSheet(false);
  };
  const closeDeclineBottomSheet = () => {
    if (submitting) return;
    setIsShowDeclineBottomSheet(false);
  };
  useEffect(() => {
    if (confirmed && !submitting) {
      setIsShowAcceptBottomSheet(false);
      setConfirmed(false);
    }
  }, [confirmed, submitting]);

  const handleSelectReason = (reasonValue: RepprovalReason) => {
    setSelectedReason(reasonValue);
  };

  const renderAcceptBottomSheet = () => {
    if (!onConfirm) return;

    return (
      <ConfirmationModal
        showCancelButton={true}
        open={isShowAcceptBottomSheet}
        title={t('travelApproval.modal.title')}
        onClose={closeAcceptBottomSheet}
        onConfirm={() => {
          setConfirmed(true);
          if (onConfirm) onConfirm();
        }}
        submitting={submitting}
      >
        <TextModal>{htmlT(t, 'travelApproval.modal.textApprove', { name: requesterName })}</TextModal>
      </ConfirmationModal>
    );
  };

  const renderDeclineBottomSheet = () => {
    if (!onDecline) return;

    return (
      <ConfirmationModal
        showCancelButton={true}
        open={isShowDeclineBottomSheet}
        title={t('travelApproval.modal.title')}
        onClose={closeDeclineBottomSheet}
        onConfirm={() => onDecline(selectedReason)}
        submitting={submitting}
        isModal={false}
      >
        <>
          <TextModal>{htmlT(t, 'travelApproval.modal.textReject', { name: requesterName })}</TextModal>
          {reasons && (
            <>
              <br />
              <CustomSelect
                name="reprovalReasons"
                onChange={handleSelectReason}
                placeHolder={t('travelApproval.modal.selectReprovalReasonPlaceholder')}
                selectRef={reasonRef}
                value={selectedReason}
              >
                {({ ...selectProps }: CustomSelectRenderProps) => (
                  <OptionsItems {...selectProps} selectRef={reasonRef} reasons={reasons} />
                )}
              </CustomSelect>
            </>
          )}
        </>
      </ConfirmationModal>
    );
  };

  return (
    <>
      {onDecline && onConfirm && (
        <Container>
          <Button invertedColor onClick={openDeclineBottomSheet}>
            {t('travelDetails.declineButton')}
          </Button>

          <AcceptButton onClick={openAcceptBottomSheet}>
            {paymentFailure === true ? t('requestCard.retryApproval') : t('travelDetails.acceptButton')}
          </AcceptButton>
        </Container>
      )}
      {renderAcceptBottomSheet()}
      {renderDeclineBottomSheet()}
    </>
  );
};

export default Buttons;
